import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { getCrimeDisplayName, weightedPercentages } from '../../constants/crimes';
import { getStateAbbreviation } from '../../constants/locations';
import { formatNumber, formatRank } from '../../utils/formatting';
import { getScoreColor } from '../../utils/coloring';
import ScoreMethodologyModal from '../Modals/ScoreMethodologyModal';
import './Charts.css';

const ScoreGauge = ({ 
  score, 
  label, 
  rank, 
  weight,
  locationType, 
  containerWidth, 
  isFirstBox, 
  compareMode, 
  stateAbbr, 
  isCountyOrCity,
  isMobile, 
  isSmallMobile, 
  selectedLocationName
 }) => {
  const [isMethodologyModalOpen, setIsMethodologyModalOpen] = useState(false);
  const [animatedAngle, setAnimatedAngle] = useState(0);
  const [animatedScore, setAnimatedScore] = useState(0);
  const radius = Math.min(containerWidth / 5, 100);
  const targetAngle = (score / 100) * 180;
  const strokeWidth = radius * 0.15;

  useEffect(() => {
    setAnimatedAngle(0);
    setAnimatedScore(0);

    if (score === null || score === undefined || isNaN(score)) {
      return;
    }
    
    const duration = Math.max(1000, (score / 100) * 2000); 
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      
      setAnimatedAngle(targetAngle * easeProgress);
      setAnimatedScore(score * easeProgress);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [score, targetAngle, selectedLocationName]);

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      width: '100%',
      height: '100%',
      padding: '10px',
      boxSizing: 'border-box',
      gap: '10px'
    }}>
      <div style={{ 
        fontSize: '14px', 
        fontWeight: 600, 
        textAlign: 'center',
        width: '100%',
        marginBottom: '4px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative'
      }}>
        <span>
          {isFirstBox 
            ? `All Crime` 
            : label === 'KRIME Score' && isCountyOrCity
              ? <>{compareMode === 'state' ? stateAbbr : 'US'} KRIME Score&trade;</>
              : label === 'KRIME Score'
                ? <>KRIME Score&trade;</>
                : label
          }
        </span>
      </div>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        flex: 1,
        position: 'relative'
      }}>
        <div style={{ 
          position: 'relative', 
          width: radius * 2, 
          height: radius,
          margin: '0 auto'
        }}>
          <svg width={radius * 2} height={radius} style={{ overflow: 'visible' }}>
            <path
              d={`M ${strokeWidth/2},${radius} A ${radius-strokeWidth/2},${radius-strokeWidth/2} 0 0 1 ${radius*2-strokeWidth/2},${radius}`}
              fill="none"
              stroke="var(--border-color)"
              strokeWidth={strokeWidth}
            />
            {Array.from({ length: 10 }).map((_, i) => {
              const segmentStartAngle = i * 18;
              const segmentEndAngle = (i + 1) * 18;
              
              const startX = radius + (radius - strokeWidth/2) * Math.cos((segmentStartAngle - 180) * Math.PI / 180);
              const startY = radius + (radius - strokeWidth/2) * Math.sin((segmentStartAngle - 180) * Math.PI / 180);
              const endX = radius + (radius - strokeWidth/2) * Math.cos((segmentEndAngle - 180) * Math.PI / 180);
              const endY = radius + (radius - strokeWidth/2) * Math.sin((segmentEndAngle - 180) * Math.PI / 180);
              
              const largeArcFlag = (segmentEndAngle - segmentStartAngle) > 180 ? 1 : 0;
              
              return (
                <path
                  key={`background-${i}`}
                  d={`M ${startX},${startY} A ${radius-strokeWidth/2},${radius-strokeWidth/2} 0 ${largeArcFlag} 1 ${endX},${endY}`}
                  fill="none"
                  stroke={getScoreColor(i * 10)}
                  strokeWidth={strokeWidth}
                  style={{ opacity: 0.4 }}
                />
              );
            })}
            {Array.from({ length: 10 }).map((_, i) => {
              const segmentStartAngle = i * 18;
              const segmentEndAngle = (i + 1) * 18;
              const visibleEndAngle = Math.min(segmentEndAngle, animatedAngle);
              
              if (visibleEndAngle <= segmentStartAngle) return null;
              
              const startX = radius + (radius - strokeWidth/2) * Math.cos((segmentStartAngle - 180) * Math.PI / 180);
              const startY = radius + (radius - strokeWidth/2) * Math.sin((segmentStartAngle - 180) * Math.PI / 180);
              const endX = radius + (radius - strokeWidth/2) * Math.cos((visibleEndAngle - 180) * Math.PI / 180);
              const endY = radius + (radius - strokeWidth/2) * Math.sin((visibleEndAngle - 180) * Math.PI / 180);
              
              const largeArcFlag = (visibleEndAngle - segmentStartAngle) > 180 ? 1 : 0;
              
              return (
                <path
                  key={`animated-${i}`}
                  d={`M ${startX},${startY} A ${radius-strokeWidth/2},${radius-strokeWidth/2} 0 ${largeArcFlag} 1 ${endX},${endY}`}
                  fill="none"
                  stroke={getScoreColor(i * 10)}
                  strokeWidth={strokeWidth}
                  style={{ opacity: 0.8 }}
                />
              );
            })}
            {animatedAngle > 0 && (
              <g>
                <line
                  x1={radius + (radius - strokeWidth * 1.4) * Math.cos((animatedAngle - 180) * Math.PI / 180)}
                  y1={radius + (radius - strokeWidth * 1.4) * Math.sin((animatedAngle - 180) * Math.PI / 180)}
                  x2={radius + (radius + strokeWidth * 0.4) * Math.cos((animatedAngle - 180) * Math.PI / 180)}
                  y2={radius + (radius + strokeWidth * 0.4) * Math.sin((animatedAngle - 180) * Math.PI / 180)}
                  stroke={score !== null && score !== undefined && !isNaN(score) ? getScoreColor(animatedScore) : 'var(--text-color)'}
                  strokeWidth={2}
                  strokeLinecap="round"
                />
              </g>
            )}
          </svg>
          <div style={{ 
            position: 'absolute', 
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%'
          }}>
            <div style={{
              fontSize: '24px',
              fontWeight: 600,
              color: score !== null && score !== undefined && !isNaN(score) ? getScoreColor(animatedScore) : 'var(--text-color)',
              fontStyle: label === 'KRIME Score' ? 'normal' : 'italic'
            }}>
              {score !== null && score !== undefined && !isNaN(score) ? formatNumber(animatedScore, true) : '-'}
            </div>
          </div>
          <div style={{
            position: 'absolute',
            bottom: label === 'KRIME Score' && isMobile ? '-2px' : '4px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: label === 'KRIME Score' && isSmallMobile ? '10px' : (isMobile ? '11px' : '12px'),
            color: label === 'KRIME Score' ? 'var(--text-color)' : 'var(--silver)',
            textAlign: 'center',
            width: '70%'
          }}>
            {label === 'KRIME Score' ? 
              'Composite Score for each type of crime' : 
                <>
                  <em><strong>Weight: </strong>
                    {(label === 'All Crime' || label === 'Violent Crime' || label === 'Property Crime') ? (
                      <span 
                        className="footnote-link-scores-chart" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsMethodologyModalOpen(true);
                        }}
                      >
                        {weight}<strong>*</strong>
                      </span>
                    ) : (
                      <span>{weight}</span>
                    )}
                  </em>
                </>
            }
          </div>
        </div>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          gap: '4px',
          fontSize: '12px',
          width: radius * 2,
          marginTop: '10px',
          whiteSpace: 'nowrap'
        }}>
          <div style={{ fontSize: isSmallMobile ? '11px' : '12px' }}>
            <strong>{isCountyOrCity ? `${compareMode === 'state' ? stateAbbr : 'US'}` : ''} {locationType} Rank: </strong> 
            <span style={{ fontStyle: label === 'KRIME Score' ? 'normal' : 'italic' }}>
              {rank !== null && rank !== undefined ? formatRank(rank) : '-'}
            </span>
          </div>
        </div>
      </div>

      <ScoreMethodologyModal 
        isOpen={isMethodologyModalOpen} 
        onClose={() => setIsMethodologyModalOpen(false)} 
      />
    </div>
  );
};

const ScoresChart = ({ 
  selectedLocation, 
  selectedCrime, 
  compareMode, 
  onCompareModeChange 
}) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 420);
  const containerRef = useRef(null);

  const current = selectedLocation?.data?.current;
  const locationType = selectedLocation.info?.type?.charAt(0).toUpperCase() + selectedLocation.info?.type?.slice(1);
  const isNation = selectedLocation.info?.type?.toLowerCase() === 'nation';
  const isCounty = selectedLocation.info?.type?.toLowerCase() === 'county';
  const isCity = selectedLocation.info?.type?.toLowerCase() === 'city';
  const isCountyOrCity = isCounty || isCity;
  
  const stateAbbr = React.useMemo(() => {
    if (isCountyOrCity && selectedLocation?.info?.name?.includes(',')) {
      const stateName = selectedLocation.info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    return getStateAbbreviation(selectedLocation);
  }, [isCountyOrCity, selectedLocation]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
      setIsSmallMobile(window.innerWidth <= 420);
    };

    const handleOrientationChange = () => {
      handleResize();
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  useLayoutEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
  
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  if (selectedLocation?.loading || !selectedLocation?.data?.current || selectedLocation?.info?.name?.startsWith('All ')) {
    return null;
  }

  const hasCrimeData = [
    'all_crime_count', 'violent_crime_count', 'property_crime_count',
    'murder_count', 'rape_count', 'robbery_count', 'aggravated_assault_count',
    'burglary_count', 'larceny_theft_count', 'motor_vehicle_theft_count'
  ].some(field => 
    current[field] !== null && 
    current[field] !== undefined && 
    current[field] !== 'N/A'
  );
  
  if (!hasCrimeData) {
    return <div className="loading">No Data Available</div>;
  }

  if (isNation) {
    return (
      <div className="loading">
        Scores are not available for the entire nation
      </div>
    );
  }
  
  const getScoreFieldKey = (prefix) => {
    if (!isCountyOrCity) return `${prefix}_score`;
    
    return compareMode === 'state' 
      ? `${prefix}_state_score` 
      : `${prefix}_national_score`;
  };
  
  const getScoreRankFieldKey = (prefix) => {
    if (!isCountyOrCity) return `${prefix}_score_rank`;
    
    return compareMode === 'state' 
      ? `${prefix}_state_score_rank` 
      : `${prefix}_national_score_rank`;
  };
  
  const getCompositeFieldKey = (baseField) => {
    if (!isCountyOrCity) return baseField;
    
    return compareMode === 'state'
      ? `state_${baseField}`
      : `national_${baseField}`;
  };

  const scores = [
    { 
      label: 'KRIME Score', 
      score: current[getCompositeFieldKey('composite_score')],
      rank: current[getCompositeFieldKey('composite_score_rank')],
      weight: '100%',
      size: 'large'
    },
    { 
      label: 'All Crime', 
      score: current[getScoreFieldKey('all_crime')],
      rank: current[getScoreRankFieldKey('all_crime')],
      weight: weightedPercentages.all_crime,
      size: 'large'
    },
    { 
      label: 'Violent Crime', 
      score: current[getScoreFieldKey('violent_crime')],
      rank: current[getScoreRankFieldKey('violent_crime')],
      weight: weightedPercentages.violent_crime,
      size: 'large'
    },
    { 
      label: 'Property Crime', 
      score: current[getScoreFieldKey('property_crime')],
      rank: current[getScoreRankFieldKey('property_crime')],
      weight: weightedPercentages.property_crime,
      size: 'large'
    },
    { 
      label: 'Murder', 
      score: current[getScoreFieldKey('murder')],
      rank: current[getScoreRankFieldKey('murder')],
      weight: weightedPercentages.murder,
      size: 'small'
    },
    { 
      label: 'Rape', 
      score: current[getScoreFieldKey('rape')],
      rank: current[getScoreRankFieldKey('rape')],
      weight: weightedPercentages.rape,
      size: 'small'
    },
    { 
      label: 'Burglary', 
      score: current[getScoreFieldKey('burglary')],
      rank: current[getScoreRankFieldKey('burglary')],
      weight: weightedPercentages.burglary,
      size: 'small'
    },
    { 
      label: 'Larceny/Theft', 
      score: current[getScoreFieldKey('larceny_theft')],
      rank: current[getScoreRankFieldKey('larceny_theft')],
      weight: weightedPercentages.larceny_theft,
      size: 'small'
    },
    { 
      label: 'Robbery', 
      score: current[getScoreFieldKey('robbery')],
      rank: current[getScoreRankFieldKey('robbery')],
      weight: weightedPercentages.robbery,
      size: 'small'
    },
    { 
      label: 'Aggravated Assault', 
      score: current[getScoreFieldKey('aggravated_assault')],
      rank: current[getScoreRankFieldKey('aggravated_assault')],
      weight: weightedPercentages.aggravated_assault,
      size: 'small'
    },
    { 
      label: 'Motor Vehicle Theft', 
      score: current[getScoreFieldKey('motor_vehicle_theft')],
      rank: current[getScoreRankFieldKey('motor_vehicle_theft')],
      weight: weightedPercentages.motor_vehicle_theft,
      size: 'small'
    }
  ];

  const isSelectedCrime = (label) => {
    return label === getCrimeDisplayName(selectedCrime) || 
           (selectedCrime === 'all_crime' && label === 'All Crime');
  };

  return (
    <div className="chart-container scores-chart" ref={containerRef}>
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        width: '100%',
      }}>
        <div style={{
          gridColumn: '1 / -1',
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          borderBottom: '1px solid var(--border-color)'
        }}>
          {scores.slice(0, 2).map((score, i) => (
            <div 
              key={score.label}
              style={{
                borderTop: '2px solid var(--border-color)',
                borderLeft: i === 0 ? '1px solid var(--border-color)' : 'none',
                borderRight: '1px solid var(--border-color)',
                background: isSelectedCrime(score.label)
                  ? '#fef9c3'
                  : score.label === 'KRIME Score'
                    ? 'linear-gradient(rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)) var(--navy)'
                    : 'var(--light-gray)'
              }}>
              <ScoreGauge 
                {...score} 
                locationType={locationType}
                containerWidth={containerWidth}
                isFirstBox={i === 1}
                compareMode={compareMode}
                stateAbbr={stateAbbr}
                isCountyOrCity={isCountyOrCity}
                isMobile={isMobile}
                isSmallMobile={isSmallMobile}
                selectedLocationName={selectedLocation?.info?.name}
              />
            </div>
          ))}
        </div>
        <div style={{
          gridColumn: '1 / -1',
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          borderBottom: '1px solid var(--border-color)'
        }}>
          {scores.slice(2, 4).map((score, i) => (
            <div 
              key={score.label}
              style={{
                borderLeft: i === 0 ? '1px solid var(--border-color)' : 'none',
                borderRight: '1px solid var(--border-color)',
                background: isSelectedCrime(score.label)
                  ? '#fef9c3'
                  : 'var(--light-gray)'
              }}>
              <ScoreGauge 
                {...score} 
                locationType={locationType}
                containerWidth={containerWidth}
                isFirstBox={false}
                compareMode={compareMode}
                stateAbbr={stateAbbr}
                isCountyOrCity={isCountyOrCity}
                isMobile={isMobile}
                isSmallMobile={isSmallMobile}
                selectedLocationName={selectedLocation?.info?.name}
              />
            </div>
          ))}
        </div>
        
        <div style={{ 
          gridColumn: '1 / -1',
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          width: '100%'
        }}>
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            borderLeft: '1px solid var(--border-color)',
            borderRight: '1px solid var(--border-color)'
          }}>
            <div style={{
              borderBottom: '1px solid var(--border-color)',
              background: isSelectedCrime(scores[4].label) ? '#fef9c3' : 'white'
            }}>
              <ScoreGauge 
                {...scores[4]} 
                locationType={locationType}
                containerWidth={containerWidth}
                isFirstBox={false}
                compareMode={compareMode}
                stateAbbr={stateAbbr}
                isCountyOrCity={isCountyOrCity}
                isMobile={isMobile}
                isSmallMobile={isSmallMobile}
                selectedLocationName={selectedLocation?.info?.name}
              />
            </div>
            <div style={{
              borderBottom: '1px solid var(--border-color)',
              background: isSelectedCrime(scores[5].label) ? '#fef9c3' : 'white'
            }}>
              <ScoreGauge 
                {...scores[5]} 
                locationType={locationType}
                containerWidth={containerWidth}
                isFirstBox={false}
                compareMode={compareMode}
                stateAbbr={stateAbbr}
                isCountyOrCity={isCountyOrCity}
                isMobile={isMobile}
                isSmallMobile={isSmallMobile}
                selectedLocationName={selectedLocation?.info?.name}
              />
            </div>
            <div style={{
              borderBottom: '1px solid var(--border-color)',
              background: isSelectedCrime(scores[8].label) ? '#fef9c3' : 'white'
            }}>
              <ScoreGauge 
                {...scores[8]} 
                locationType={locationType}
                containerWidth={containerWidth}
                isFirstBox={false}
                compareMode={compareMode}
                stateAbbr={stateAbbr}
                isCountyOrCity={isCountyOrCity}
                isMobile={isMobile}
                isSmallMobile={isSmallMobile}
                selectedLocationName={selectedLocation?.info?.name}
              />
            </div>                
            <div style={{
              borderBottom: '1px solid var(--border-color)',
              background: isSelectedCrime(scores[9].label) ? '#fef9c3' : 'white'
            }}>
              <ScoreGauge 
                {...scores[9]} 
                locationType={locationType}
                containerWidth={containerWidth}
                isFirstBox={false}
                compareMode={compareMode}
                stateAbbr={stateAbbr}
                isCountyOrCity={isCountyOrCity}
                isMobile={isMobile}
                isSmallMobile={isSmallMobile}
                selectedLocationName={selectedLocation?.info?.name}
              />
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{
              borderBottom: '1px solid var(--border-color)',
              borderRight: '1px solid var(--border-color)',
              background: isSelectedCrime(scores[6].label) ? '#fef9c3' : 'white'
            }}>
              <ScoreGauge 
                {...scores[6]} 
                locationType={locationType}
                containerWidth={containerWidth}
                isFirstBox={false}
                compareMode={compareMode}
                stateAbbr={stateAbbr}
                isCountyOrCity={isCountyOrCity}
                isMobile={isMobile}
                isSmallMobile={isSmallMobile}
                selectedLocationName={selectedLocation?.info?.name}
              />
            </div>                
            <div style={{
              borderBottom: '1px solid var(--border-color)',
              borderRight: '1px solid var(--border-color)',
              background: isSelectedCrime(scores[7].label) ? '#fef9c3' : 'white'
            }}>
              <ScoreGauge 
                {...scores[7]} 
                locationType={locationType}
                containerWidth={containerWidth}
                isFirstBox={false}
                compareMode={compareMode}
                stateAbbr={stateAbbr}
                isCountyOrCity={isCountyOrCity}
                isMobile={isMobile}
                isSmallMobile={isSmallMobile}
                selectedLocationName={selectedLocation?.info?.name}
              />
            </div>
            <div style={{
              borderBottom: '1px solid var(--border-color)',
              borderRight: '1px solid var(--border-color)',
              background: isSelectedCrime(scores[10].label) ? '#fef9c3' : 'white'
            }}>
              <ScoreGauge 
                {...scores[10]} 
                locationType={locationType}
                containerWidth={containerWidth}
                isFirstBox={false}
                compareMode={compareMode}
                stateAbbr={stateAbbr}
                isCountyOrCity={isCountyOrCity}
                isMobile={isMobile}
                isSmallMobile={isSmallMobile}
                selectedLocationName={selectedLocation?.info?.name}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoresChart;